import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
const DEVICE_WIDTH = { mobile: 390, tablet: 820, desktop: 1280 };
const Preview = forwardRef(function Preview(props, ref) {
    const { publicBase, slug, reloadKey = 0, selectedId, onClickInIframe, editMode = false, onEditModeChange, onInlineMessage, onReady, } = props;
    const [device, setDevice] = useState('desktop');
    const [bumpKey, setBumpKey] = useState(0);
    const [ready, setReady] = useState(false);
    const iframeRef = useRef(null);
    const url = useMemo(() => {
        const base = publicBase.replace(/\/+$/, '');
        const path = slug === 'home' ? base + '/' : `${base}/${encodeURIComponent(slug)}`;
        const sep = path.includes('?') ? '&' : '?';
        return `${path}${sep}_preview=1${editMode ? '&_edit=1' : ''}`;
    }, [publicBase, slug, editMode]);
    // Reload only on: slug change, edit-mode flip, or explicit external bump
    // (Save/Undo/Redo). Setting changes do NOT trigger a reload — the parent
    // posts patch messages instead.
    useEffect(() => { setBumpKey((k) => k + 1); setReady(false); }, [reloadKey, slug, editMode]);
    // Listen for clicks inside the iframe + forward inline:* events to parent.
    useEffect(() => {
        function onMessage(e) {
            const data = e.data;
            if (!data || typeof data !== 'object')
                return;
            if (data.type === 'preview:ready') {
                setReady(true);
                onReady?.();
            }
            if (data.type === 'preview:click' && typeof data.id === 'string') {
                onClickInIframe?.(data.id);
            }
            if (typeof data.type === 'string' && data.type.startsWith('inline:')) {
                onInlineMessage?.(data);
            }
        }
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [onClickInIframe, onInlineMessage, onReady]);
    function postToIframe(msg) {
        iframeRef.current?.contentWindow?.postMessage(msg, '*');
    }
    useImperativeHandle(ref, () => ({
        postPatch: (msg) => postToIframe(msg),
        reload: () => { setBumpKey((k) => k + 1); setReady(false); },
    }), []);
    // Push selection into the iframe so it highlights/scrolls.
    useEffect(() => {
        if (!ready)
            return;
        postToIframe({ type: 'preview:scroll-to', id: selectedId ?? null });
    }, [ready, selectedId]);
    return (_jsxs("div", { className: "preview", children: [_jsxs("div", { className: "preview-toolbar", children: [_jsx("div", { className: "device-toggle", role: "tablist", children: ['mobile', 'tablet', 'desktop'].map((d) => (_jsxs("button", { role: "tab", "aria-selected": device === d, className: device === d ? 'active' : '', onClick: () => setDevice(d), children: [d, _jsx("span", { className: "px", children: DEVICE_WIDTH[d] })] }, d))) }), _jsx("div", { className: "preview-url", title: url, children: url }), onEditModeChange && (_jsx("button", { className: editMode ? 'preview-edit-toggle active' : 'preview-edit-toggle ghost', onClick: () => onEditModeChange(!editMode), title: editMode ? 'Stop editing — return to clean preview' : 'Click any text to edit it inline', children: editMode ? '● Editing' : '✎ Edit' })), _jsx("button", { className: "ghost", onClick: () => { setBumpKey((k) => k + 1); setReady(false); }, children: "\u21BB Reload" })] }), _jsx("div", { className: "preview-stage", children: _jsx("iframe", { ref: iframeRef, title: `Preview: ${slug}`, src: url, style: {
                        width: device === 'desktop' ? '100%' : `${DEVICE_WIDTH[device]}px`,
                        maxWidth: '100%',
                    } }, bumpKey) })] }));
});
export default Preview;
